import { getIntlLocale } from '../../i18n/i18n';

/**
 * Display unit of a recipe entered in bales (`recipe_in_bales`), resolved to a translated label at render time.
 *
 * Recipes are always stored as percentages (`ratio`); a bales recipe also saves the whole `bales` next to
 * each ratio, and this app shows those saved bales as entered. It never derives bales from ratios.
 */
export const BALES_UNIT = 'bales';

/**
 * Tolerance used when comparing floating point numeric values in recipe corrections.
 */
export const NUMERIC_COMPARISON_EPSILON = 0.000001;

/**
 * Converts unknown numeric-like input to a finite number.
 *
 * @param {any} value - Value to parse.
 * @param {number} [fallback=0] - Returned when parsing fails.
 * @returns {number} Finite numeric value.
 */
export function parseNumeric(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Formats a measure value with localized grouping/decimal separators and a unit suffix.
 *
 * Production types display different units (`t` for batching weights, `%` for continuous
 * ratios), so the unit is supplied by the caller rather than hard-coded.
 *
 * @param {any} value - Raw numeric-like value.
 * @param {string} [unit='t'] - Display unit suffix (e.g. `t`, `%`).
 * @param {string} [languageCode] - App language code; its number locale comes from i18n.
 * @returns {string} Localized measure label, e.g. `1.25 t` or `45 %`.
 */
export function formatMeasure(value, unit = 't', languageCode) {
  const locale = getIntlLocale(languageCode);

  return `${parseNumeric(value, 0).toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} ${unit}`;
}

/**
 * Resolve the label shown after a measure value: translated and pluralized for bales, the raw unit
 * (`t`, `%`) otherwise.
 *
 * @param {string} unit - Display unit of the item.
 * @param {any} value - Displayed value; its magnitude picks singular or plural (`-1 bale`).
 * @param {Function} t - Translation function.
 * @returns {string} Unit label, e.g. `bale`, `bales`, `%`.
 */
function getUnitLabel(unit, value, t) {
  if (unit !== BALES_UNIT) return unit;
  return t(Math.abs(parseNumeric(value, 0)) === 1 ? 'recipe_unit_bale' : 'recipe_unit_bales');
}

/**
 * Formats a measure value with its display label: `formatMeasure` with the unit translated and
 * pluralized for bales (`1 bale`, `-2 bales`) and passed through otherwise (`45 %`, `1.5 t`).
 *
 * @param {any} value - Raw numeric-like value.
 * @param {string} unit - Display unit of the item (`t`, `%`, `bales`).
 * @param {string} languageCode - Language code or locale.
 * @param {Function} t - Translation function.
 * @returns {string} Localized measure label.
 */
export function formatMeasureLabel(value, unit, languageCode, t) {
  return formatMeasure(value, getUnitLabel(unit, value, t), languageCode);
}
